import { gql, useQuery } from '@apollo/client';
import { useMemo } from 'react';

import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import type {
  BillingPlanKind,
  UseCurrentPlanQuery,
  UseCurrentPlanQueryVariables,
} from 'src/lib/graphqlTypes/types';

interface Args extends UseCurrentPlanQueryVariables {
  skip?: boolean;
}

const isAccountTypeCloudy = (billingPlanKind: BillingPlanKind | undefined) => {
  if (billingPlanKind) {
    return [
      'DEDICATED',
      'SERVERLESS',
      'SERVERLESS_FREE',
      'SERVERLESS_PAID',
    ].includes(billingPlanKind.toString());
  } else {
    return false;
  }
};

const isAccountTypeEnterprise = (
  billingPlanKind: BillingPlanKind | undefined,
) => {
  if (billingPlanKind) {
    return [
      'ENTERPRISE_INTERNAL',
      'ENTERPRISE_PAID',
      'ENTERPRISE_PILOT',
      'ENTERPRISE_TRIAL',
    ].includes(billingPlanKind.toString());
  } else {
    return false;
  }
};

export const USE_CURRENT_PLAN_QUERY = gql<
  UseCurrentPlanQuery,
  UseCurrentPlanQueryVariables
>/* GraphQL */ `
  query UseCurrentPlanQuery($accountId: ID!) {
    account(id: $accountId) {
      id
      isOnExpiredTrial
      isLocked
      capabilities {
        clients
        clientVersions
        contracts
        datadog
        launches
        notifications
        persistedQueries
        traces
        userRoles
        webhooks
      }
      limits {
        maxRangeInDays
        maxAuditInDays
        maxRangeInDaysForChecks
        maxRequestsPerMonth
      }
      currentPlan {
        id
        billingModel
        billingPeriod
        kind
        isTrial
        name
        ranges
        tier
        provider
      }
    }
  }
`;

/**
 * @deprecated Avoid using this custom hook to query for data as it will lead to over-fetching. You should query for the data you need directly.
 */
export function useCurrentPlan({ skip = false, ...variables }: Args) {
  const currentPlanResult = useQuery<
    UseCurrentPlanQuery,
    UseCurrentPlanQueryVariables
  >(USE_CURRENT_PLAN_QUERY, {
    variables,
    skip,
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });

  return useMemo(() => {
    const account = currentPlanResult.data?.account;
    const currentPlan = account?.currentPlan;
    const capabilities = account?.capabilities;
    const limits = account?.limits;

    return {
      currentPlan: currentPlan && {
        ...(currentPlan ?? {}),
        ...(capabilities ?? {}),
        ...(limits ?? {}),
      },
      capabilities,
      limits,
      isOnExpiredTrial: account?.isOnExpiredTrial ?? false,
      isCloudyAccountType: isAccountTypeCloudy(currentPlan?.kind),
      isEnterpriseAccount: isAccountTypeEnterprise(currentPlan?.kind),
      isLocked: account?.isLocked,
      loading: currentPlanResult.loading,
      error: currentPlanResult.error,
    };
  }, [currentPlanResult]);
}
